import React, { useState, useEffect, useLayoutEffect, createContext, useContext, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import apiClient from "./api/client";
import "./App.css";
import Dashboard from "./components/Dashboard";
import Statistics from "./components/Statistics";
import { useGroupedSyminfo, useDailySignals, usePredictions, useStockDetail, useROI, useTradeSignals } from "./api/hooks";
import SidebarStockList from "./components/SidebarStockList";
import CandlestickChart from "./components/CandlestickChart";
import RoiPerformanceTable from "./components/RoiPerformanceTable";
import DailyComment from "./components/DailyComment";
import StockStatisticsAccordion from "./components/StockStatisticsAccordion";
import ModelMetricsTable from "./components/ModelMetricsTable";
import About from "./components/About";
import Contact from "./components/Contact";
import PrivacyPolicy from "./components/PrivacyPolicy";
import CookiePolicy from "./components/CookiePolicy";
import TermsOfService from "./components/TermsOfService";
import CookieConsentGate from "./components/CookieConsentGate";
import { hasEssentialConsent } from "./utils/cookieConsent";
import { computeHeaderPrice } from "./utils/premarketQuote";
import Portfolio from "./components/Portfolio";
import { AuthProvider, useAuth } from "./context/AuthContext";
import SignIn from "./components/SignIn";
import SignUp from "./components/SignUp";
import ConfirmAccount from "./components/ConfirmAccount";
import ForgotPassword from "./components/ForgotPassword";

// Icons
import {
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  SunIcon,
  MoonIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowRightIcon,
  CurrencyDollarIcon,
  BriefcaseIcon,
  CogIcon
} from "@heroicons/react/24/outline";

import useAppVisibility from './hooks/useAppVisibility';

// Helpers available across components
export const displayNameFor = (value) => {
  try {
    const s = String(value || '').toUpperCase();
    const map = {
      SP500: 'S&P 500',
      NASDAQ: 'NASDAQ',
      NYSE: 'NYSE',
      BIST: 'BIST',
      CRYPTO: 'Crypto',
      FOREX: 'Forex',
      COMMODITIES: 'Commodities',
      ETF: 'ETFs',
      USALL: 'US All',
    };
    return map[s] || s || '-';
  } catch {
    return String(value || '-')
  }
};

export const formatCET = (value) => {
  try {
    if (!value) return '-';
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      const dateStr = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: '2-digit',
        timeZone: 'Europe/Berlin',
      }).format(d);
      const timeStr = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Europe/Berlin',
      }).format(d);
      return `${dateStr}, ${timeStr}`;
    }
    const m = String(value).match(/^([A-Za-z]+)\s+(\d{1,2}),?\s*(?:\d{4})?\s*(?:at\s*)?(\d{2}):(\d{2})/);
    if (m) {
      const full = m[1];
      const day = String(Number(m[2]));
      const hh = m[3];
      const mm = m[4];
      const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const idx = months.findIndex((x) => x.toLowerCase() === full.toLowerCase());
      const short = idx >= 0 ? ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][idx] : full.slice(0,3);
      return `${short} ${day}, ${hh}:${mm}`;
    }
    return String(value);
  } catch {
    return '-';
  }
};

const isDev = process.env.NODE_ENV === 'development';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// API URL: use backend URL in dev, relative in prod
const API = isDev
  ? `${BACKEND_URL.replace(/\/$/, '')}/api`
  : '/api';

// Google Analytics (GA4) Measurement ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Lightweight GA loader using gtag.js; safe to call multiple times
function loadGAScript(measurementId) {
  try {
    // Create and configure the script tag
    const s = document.createElement('script');
    s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    s.async = true;
    s.onload = () => {
      // Initialize gtag once the script is loaded
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', measurementId, {
        page_path: window.location.pathname,
        transport_type: 'beacon',
        debug_mode: isDev,
      });
    };
    s.onerror = () => console.warn('GA gtag.js failed to load (possibly blocked by an ad/tracker blocker).');
    document.head.appendChild(s);
  } catch {}
}

// Scroll restoration to top on route (and query) changes
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    // Reset window scroll
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      // Also reset any scroll on the main content area if it has its own overflow
      const main = document.querySelector('main');
      if (main) main.scrollTop = 0;
    } catch {}

    // Google Analytics page view for SPA route changes
    try {
      if (GA_MEASUREMENT_ID) {
        loadGAScript(GA_MEASUREMENT_ID);
        if (typeof window.gtag === 'function') {
          const page_path = `${location.pathname}${location.search}`;
          const page_title = document.title || 'BorsAI';
          const page_location = window.location.href;
          window.gtag('config', GA_MEASUREMENT_ID, {
            page_path,
            page_title,
            page_location,
            transport_type: 'beacon',
            debug_mode: isDev,
          });
        }
      }
    } catch {}
  }, [location.pathname, location.search]);
  return null;
};

// Theme Context
const ThemeContext = createContext();

const THEME_LS_KEY = 'borsai.theme.v1';
const THEME_SOURCE_LS_KEY = 'borsai.theme.source.v1'; // 'user' | 'server'

const normalizeTheme = (raw) => (raw === 'dark' || raw === 'light' ? raw : null);

const readThemeFromLocalStorage = () => {
  try {
    const theme = normalizeTheme(localStorage.getItem(THEME_LS_KEY));
    const sourceRaw = localStorage.getItem(THEME_SOURCE_LS_KEY);
    // Back-compat: if theme exists but no source, assume it was explicitly set by the user.
    const source = sourceRaw === 'user' || sourceRaw === 'server' ? sourceRaw : (theme ? 'user' : null);
    return { theme, source };
  } catch {
    return { theme: null, source: null };
  }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Site-wide footer disclaimer
const FooterDisclaimer = () => {
  const { theme } = useTheme();
  return (
    <footer
      className={`px-4 py-2 text-sm text-center ${
        theme === 'dark' ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-700'
      }`}
    >
    <div className="px-6 py-6 lg:ml-[13.5rem]">
      <div
        className={`max-w-5xl text-sm mx-auto rounded-md border ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700'
        } p-4`}
      >
        <h3 className="text-base font-semibold mb-2">Important Disclaimer</h3>
        <p className="mb-3 text-left">
          The information provided by BorsAI (hereafter referring to this website) is intended 
          solely for educational purposes. All predictions on this platform are generated entirely 
          by automated AI models and have NOT been reviewed, verified, or endorsed by a human 
          analyst before publication. BorsAI does not provide investment advice, establish advisory 
          relationships with users, or guarantee the accuracy of its predictions and analyses. Past 
          performance and model accuracy do not guarantee future results. Users are strongly 
          encouraged to conduct their own research and consult with licensed financial advisors 
          before making any investment decisions. 
        </p>
        <p className="mb-4 text-left">
          BorsAI and its team shall not be held liable for any direct or indirect losses, 
          material or moral damages, lost profits, or third-party damages that may arise 
          from the use of our platform or reliance on its content. By using this platform, 
          you acknowledge and accept these risks and limitations.
        </p>
      </div>
    </div>
      <p>
        {`© ${new Date().getFullYear()} BorsAI. All rights reserved.`}
      </p>
      <div className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs">
        <Link
          to="/privacy-policy"
          className={
            theme === 'dark'
              ? 'text-gray-400 hover:text-gray-200 underline underline-offset-2'
              : 'text-gray-700 hover:text-gray-900 underline underline-offset-2'
          }
        >
          Privacy Policy
        </Link>
        <span aria-hidden="true" className="opacity-60">|</span>
        <Link
          to="/cookie-policy"
          className={
            theme === 'dark'
              ? 'text-gray-400 hover:text-gray-200 underline underline-offset-2'
              : 'text-gray-700 hover:text-gray-900 underline underline-offset-2'
          }
        >
          Cookie Policy
        </Link>
        <span aria-hidden="true" className="opacity-60">|</span>
        <Link
          to="/terms-of-service"
          className={
            theme === 'dark'
              ? 'text-gray-400 hover:text-gray-200 underline underline-offset-2'
              : 'text-gray-700 hover:text-gray-900 underline underline-offset-2'
          }
        >
          Terms of Service
        </Link>
      </div>
    </footer>
  );
};


// Theme Provider
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const { theme: storedTheme } = readThemeFromLocalStorage();
    return storedTheme || 'dark'; // Default to dark if no preference is found
  });
  const { isAuthenticated } = useAuth();

  // Try to hydrate theme from server-side user settings.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const { ensureSettingsMigratedFromLocalStorage, rehydrateLocalStorageFromServerSettings } = await import('./context/userSettingsSync');
        const settings = await ensureSettingsMigratedFromLocalStorage();
        try { rehydrateLocalStorageFromServerSettings(settings); } catch {}
        const serverTheme = normalizeTheme(settings?.theme);

        const { theme: localTheme, source: localSource } = readThemeFromLocalStorage();

        // If the user explicitly chose a theme on this device, keep UI stable and
        // (best-effort) sync it to the server instead of overwriting.
        if (localTheme && localSource === 'user') {
          if (serverTheme !== localTheme) {
            try {
              const { persistUserSettings } = await import('./context/userSettingsSync');
              await persistUserSettings({ theme: localTheme });
            } catch {
              // ignore sync errors; keep UX stable
            }
          }
          return;
        }

        // Otherwise, prefer server theme (useful on fresh devices / new sessions).
        if (!cancelled && serverTheme) {
          setTheme(serverTheme);
          try {
            localStorage.setItem(THEME_LS_KEY, serverTheme);
            localStorage.setItem(THEME_SOURCE_LS_KEY, 'server');
          } catch {}
        }
      } catch {
        // unauthenticated or backend unavailable; keep local default
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);


  // Toggle theme and persist immediately to backend
  const toggleTheme = async () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    try {
      localStorage.setItem(THEME_LS_KEY, nextTheme);
      localStorage.setItem(THEME_SOURCE_LS_KEY, 'user');
    } catch {}
    // Persist to backend immediately
    try {
      const { persistUserSettings } = await import('./context/userSettingsSync');
      await persistUserSettings({ theme: nextTheme });
    } catch {
      // ignore errors, user can retry
    }
  };

  // Persist theme changes immediately.
  // Remove the effect that redundantly persists theme, since toggleTheme now handles it.
  // If you want to ensure hydration from backend still works, keep the hydration logic above.

  // Apply theme class before paint to avoid a light->dark flash.
  useLayoutEffect(() => {
    const root = window.document.documentElement;
    // Tailwind is configured with `darkMode: 'class'` and expects `dark` on <html>.
    // Do not add a `light` class (it is not used by Tailwind and can confuse other CSS).
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    // Helps native form controls match the theme.
    try { root.style.colorScheme = theme; } catch {}
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Loading Component
const LoadingSpinner = () => {
  const { theme } = useTheme();
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className={`animate-spin rounded-full h-32 w-32 border-b-2 ${
        theme === 'dark' ? 'border-blue-400' : 'border-blue-600'
      }`}></div>
    </div>
  );
};

const SignInRequiredOverlay = ({ open, attemptedPath, onDismiss, onSignIn, onSignUp }) => {
  const { theme } = useTheme();
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onDismiss}
        aria-hidden="true"
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          className={`w-full max-w-md rounded-xl border shadow-xl ${
            theme === 'dark'
              ? 'bg-gray-900 border-gray-700 text-white'
              : 'bg-white border-gray-200 text-gray-900'
          }`}
        >
          <div className="p-5">
            <div className="text-lg font-semibold">Sign in required</div>
            <div className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Please sign in to access that page.
            </div>
            <div className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Signing up is free — no payment information will be requested.
            </div>
            {attemptedPath && (
              <div className={`mt-3 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Attempted: <span className="font-mono">{attemptedPath}</span>
              </div>
            )}
          </div>

          <div className={`px-5 pb-5 flex flex-col sm:flex-row gap-2 sm:justify-end ${
            theme === 'dark' ? 'border-t border-gray-700' : 'border-t border-gray-200'
          } pt-4`}>
            <button
              onClick={onDismiss}
              className={`px-3 py-2 text-sm rounded-md border ${
                theme === 'dark'
                  ? 'bg-transparent border-gray-600 text-gray-200 hover:bg-gray-800'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Not now
            </button>
            <button
              onClick={onSignUp}
              className={`px-3 py-2 text-sm rounded-md border ${
                theme === 'dark'
                  ? 'bg-transparent border-blue-400 text-blue-300 hover:bg-gray-800'
                  : 'bg-white border-blue-600 text-blue-700 hover:bg-blue-50'
              }`}
            >
              Sign up
            </button>
            <button
              onClick={onSignIn}
              className={`px-3 py-2 text-sm rounded-md border ${
                theme === 'dark'
                  ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-500'
                  : 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Header Component
const Header = ({ onMenuToggle, setActiveTab }) => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = () => {
    try {
      logout();
    } finally {
      setIsProfileOpen(false);
      const publicPaths = new Set([
        '/',
        '/summary',
        '/about',
        '/contact',
        '/privacy-policy',
        '/cookie-policy',
        '/terms-of-service',
        '/signin',
        '/signup',
        '/forgot-password',
        '/confirm-account',
      ]);
      const path = location?.pathname || '/';
      const search = location?.search || '';
      const isPublic = publicPaths.has(path);

      // If user signs out from a private page, send them home.
      // If they sign out from a public page, keep them there.
      // In both cases, clear router state so we don't accidentally carry a stale
      // `location.state.from` that could trigger auth UX.
      if (!isPublic) {
        navigate('/', { replace: true, state: {} });
      } else {
        navigate(`${path}${search}`, { replace: true, state: {} });
      }
    }
  };

  return (
    <header className={`${
      theme === 'dark'
        ? 'bg-gray-800 border-gray-700'
        : 'bg-white border-gray-200'
    } border-b sticky top-0 z-50`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-12">
          {/* Left side */}
          <div className="flex items-center">
            <button
              onClick={onMenuToggle}
              className="p-2 rounded-md lg:hidden"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <div
              className="flex-shrink-0 flex items-center ml-2 lg:ml-0 cursor-pointer"
              onClick={() => {
                navigate('/');
                setActiveTab('overview'); // Reset to overview when going home
              }}
              title="Go to Home"
            >
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-xl font-bold">BorsAI</span>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-4">
            {/* Simple top banner buttons */}
            <button
              onClick={() => {
                navigate('/about');
                setActiveTab('about');
              }}
              className={`px-3 py-1 text-sm rounded-md border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              About
            </button>
            <button
              onClick={() => {
                navigate('/contact');
                setActiveTab('contact');
              }}
              className={`px-3 py-1 text-sm rounded-md border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-100'
              }`}
            >
              Contact
            </button>
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-md ${
                theme === 'dark'
                  ? 'hover:bg-gray-700 text-gray-300'
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              {theme === 'dark' ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </button>

            {!user && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    navigate('/signin', { state: { from: location } });
                  }}
                  className={`px-3 py-1 text-sm rounded-md border ${
                    theme === 'dark'
                      ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-500'
                      : 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Sign in
                </button>
                <button
                  onClick={() => {
                    navigate('/signup', { state: { from: location } });
                  }}
                  className={`px-3 py-1 text-sm rounded-md border ${
                    theme === 'dark'
                      ? 'bg-transparent border-blue-400 text-blue-300 hover:bg-gray-700'
                      : 'bg-white border-blue-600 text-blue-700 hover:bg-blue-50'
                  }`}
                >
                  Sign up
                </button>
              </div>
            )}

            {user && (
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className={`flex items-center space-x-2 p-2 rounded-md ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <UserIcon className="h-5 w-5" />
                  <span className="hidden sm:block text-sm">{user.username}</span>
                </button>

                {isProfileOpen && (
                  <div className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg ${
                    theme === 'dark'
                      ? 'bg-gray-800 border border-gray-700'
                      : 'bg-white border border-gray-200'
                  }`}>
                    <div className="py-1">
                      <button
                        onClick={handleSignOut}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          theme === 'dark'
                            ? 'hover:bg-gray-700 text-gray-300'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// Sidebar Component
const Sidebar = ({ isOpen, onClose, activeTab, setActiveTab }) => {
  const { theme } = useTheme();
  const { data: groupedSyminfo } = useGroupedSyminfo({});
  const navigate = useNavigate();
  // Context menu state for sidebar items
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, item: null });
  const menuRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!ctxMenu.open) return;
    const onDown = (e) => {
      if (!menuRef.current) return setCtxMenu({ open: false, x: 0, y: 0, item: null });
      if (!menuRef.current.contains(e.target)) setCtxMenu({ open: false, x: 0, y: 0, item: null });
    };
    const onKey = (e) => { if (e.key === 'Escape') setCtxMenu({ open: false, x: 0, y: 0, item: null }); };
    const onScroll = () => setCtxMenu({ open: false, x: 0, y: 0, item: null });
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu.open]);

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon },
    { id: 'dashboard', label: 'Dashboard', icon: ChartBarIcon },
    { id: 'portfolio', label: 'Portfolio', icon: BriefcaseIcon },
    // Statistics and Settings moved to bottom of the sidebar
    // { id: 'subscription', label: 'Subscription', icon: CurrencyDollarIcon },
  ];

  const pathFor = (id) => {
    switch (id) {
      case 'overview': return '/';
      case 'dashboard': return '/dashboard';
      case 'portfolio': return '/portfolio';
      case 'statistics': return '/statistics';
      case 'settings': return '/settings';
      default: return '/';
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={onClose}>
          <div className="fixed inset-0 bg-black opacity-50"></div>
        </div>
      )}

      {/* Sidebar: fixed on desktop (below sticky header) so it doesn't move while scrolling */}
      <div ref={containerRef} className={`fixed left-0 z-50 w-[13.5rem] transform transition-transform duration-300 ease-in-out inset-y-0 lg:top-12 lg:bottom-0 lg:translate-x-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'} overflow-y-auto`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700 lg:hidden">
          <span className="text-lg font-semibold">Menu</span>
          <button onClick={onClose}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

  <nav className="mt-8 lg:mt-1">
          <div className="px-4 space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'overview') {
                    navigate('/');
                  } else if (item.id === 'dashboard') {
                    navigate('/dashboard');
                  } else if (item.id === 'portfolio') {
                    navigate('/portfolio');
                  } else if (item.id === 'statistics') {
                    navigate('/statistics');
                  } else if (item.id === 'settings') {
                    navigate('/settings');
                  } else {
                    setActiveTab(item.id);
                  }
                  onClose();
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  try {
                    const rect = containerRef.current ? containerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
                    const x = e.clientX - rect.left;
                    const y = e.clientY - rect.top;
                    // store both relative and absolute coordinates
                    setCtxMenu({ open: true, x, y, xAbs: e.clientX, yAbs: e.clientY, item });
                  } catch {
                    setCtxMenu({ open: true, x: e.clientX, y: e.clientY, xAbs: e.clientX, yAbs: e.clientY, item });
                  }
                }}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === item.id
                    ? theme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-600 text-white'
                    : theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-800'
                      : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <item.icon className="h-5 w-5 mr-3" />
                {item.label}
              </button>
            ))}
            {/* Stock list under the buttons */}
            {(() => {
              const params = new URLSearchParams(window.location.search);
              const selectedSymbol = params.get('stock') || '';
              const selectedCategory = params.get('market') || '';
              
              const filteredGroups = { ...(groupedSyminfo || {}) };
              delete filteredGroups['DJI'];
              delete filteredGroups['SP500'];

              return (
                <SidebarStockList
                  groupedSyminfo={filteredGroups}
                  selectedSymbol={selectedSymbol}
                  selectedCategory={selectedCategory}
                  onSelect={(row) => {
                    if (row?.symbol && row?.category) {
                      navigate(`/summary?stock=${encodeURIComponent(row.symbol)}&market=${encodeURIComponent(row.category)}`);
                    }
                  }}
                />
              );
            })()}
            {/* Bottom action buttons */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 px-0 space-y-2">
              <button
                onClick={() => {
                  navigate('/statistics');
                  setActiveTab('statistics');
                  onClose();
                }}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'statistics'
                    ? theme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-600 text-white'
                    : theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-800'
                      : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ChartBarIcon className="h-5 w-5 mr-3" />
                Statistics
              </button>
              <button
                onClick={() => {
                  navigate('/settings');
                  setActiveTab('settings');
                  onClose();
                }}
                className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'settings'
                    ? theme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-600 text-white'
                    : theme === 'dark'
                      ? 'text-gray-300 hover:bg-gray-800'
                      : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <CogIcon className="h-5 w-5 mr-3" />
                Settings
              </button>
            </div>
          </div>
        </nav>
      </div>
      {/* Sidebar context menu */}
      {ctxMenu.open && ctxMenu.item && (
        <div
          ref={menuRef}
          className="z-50 min-w-[180px] rounded-md border shadow-lg text-sm bg-white dark:bg-gray-800 dark:border-gray-700"
          style={{
            position: 'fixed',
            // place top just below cursor (small offset)
            left: (() => {
              try {
                const margin = 8;
                const menuW = 200; // approximate width for clamping
                const desired = (ctxMenu.xAbs || 0) + 0;
                const maxLeft = Math.max(margin, window.innerWidth - menuW - margin);
                return Math.min(Math.max(margin, desired), maxLeft);
              } catch { return ctxMenu.x || 0; }
            })(),
            top: (() => {
              try {
                const offset = 8;
                const estimatedMenuH = 120;
                const desired = (ctxMenu.yAbs || 0) + offset;
                const maxTop = Math.max(8, window.innerHeight - estimatedMenuH - 8);
                return Math.min(Math.max(8, desired), maxTop);
              } catch { return ctxMenu.y || 0; }
            })(),
          }}
          role="menu"
        >
          <button
            type="button"
            className="block w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => {
              try {
                const path = pathFor(ctxMenu.item.id);
                const url = `${window.location.origin}${path}`;
                window.open(url, '_blank', 'noopener');
              } catch {}
              setCtxMenu({ open: false, x: 0, y: 0, item: null });
            }}
            role="menuitem"
          >
            Open in New Tab
          </button>
          <button
            type="button"
            className="block w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => {
              try {
                const path = pathFor(ctxMenu.item.id);
                const url = `${window.location.origin}${path}`;
                window.open(url, '_blank', 'noopener,width=1200,height=800');
              } catch {}
              setCtxMenu({ open: false, x: 0, y: 0, item: null });
            }}
            role="menuitem"
          >
            Open in New Window
          </button>
        </div>
      )}
    </>
  );
};

  // Overview Component
  const Overview = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const markets = ['SP500', 'DJI', 'NASDAQ', 'NYSE', 'USALL', 'BIST'];
    const aggregateMarkets = ['SP500', 'DJI', 'NASDAQ', 'NYSE', 'USALL', 'CRYPTO', 'ETF', 'FOREX', 'COMMODITIES', 'BIST'];
    const [selectedMarket, setSelectedMarket] = useState('USALL');
    const [sectorSummary, setSectorSummary] = useState([]);
    const [sectorSortField, setSectorSortField] = useState('sector');
    const [sectorSortDirection, setSectorSortDirection] = useState('asc');
    // Market Overview aggregates (sector: _AGGREGATE) across all categories
    const [marketAggregates, setMarketAggregates] = useState([]);
    const [aggSortField, setAggSortField] = useState(null); // default to original order
    const [aggSortDirection, setAggSortDirection] = useState('asc');
    const [sectorLoading, setSectorLoading] = useState(true);
    const [aggLoading, setAggLoading] = useState(true);
    // Right-click context menu state for table cells (market & sector)
    const [tableCtxMenu, setTableCtxMenu] = useState({ open: false, xAbs: 0, yAbs: 0, url: '' });
    const tableMenuRef = useRef(null);

    const signInFrom = location?.state?.from;
    const showSignInOverlay = !user && !!signInFrom;
    const [dismissedSignInOverlay, setDismissedSignInOverlay] = useState(false);

    useEffect(() => {
      if (!tableCtxMenu.open) return;
      const onDown = (e) => {
        if (!tableMenuRef.current) return setTableCtxMenu({ open: false, xAbs: 0, yAbs: 0, url: '' });
        if (!tableMenuRef.current.contains(e.target)) setTableCtxMenu({ open: false, xAbs: 0, yAbs: 0, url: '' });
      };
      const onKey = (e) => { if (e.key === 'Escape') setTableCtxMenu({ open: false, xAbs: 0, yAbs: 0, url: '' }); };
      const onScroll = () => setTableCtxMenu({ open: false, xAbs: 0, yAbs: 0, url: '' });
      document.addEventListener('mousedown', onDown, true);
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onDown, true);
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('keydown', onKey);
      };
    }, [tableCtxMenu.open]);

    useEffect(() => {
      setDismissedSignInOverlay(false);
    }, [location?.state?.from?.pathname, location?.state?.from?.search]);

    useEffect(() => {
      let isMounted = true;
      // Load sector summary for Trend Forecast Overview table
      const fetchSectorSummary = async () => {
        try {
          if (isMounted) setSectorLoading(true);
          const response = await apiClient.get('/summary/sector', { params: { category: selectedMarket } });
          const data = response?.data || {};
          // Exclude the aggregate row from Trend Forecast Overview
          const rows = Array.isArray(data) ? data.filter((d) => (d?.sector || '').toUpperCase() !== '_AGGREGATE') : [];
          if (isMounted) setSectorSummary(rows);
        } catch (e) {
          console.error('Failed to load sector summary:', e);
          if (isMounted) setSectorSummary([]);
        } finally {
          if (isMounted) setSectorLoading(false);
        }
      };
      fetchSectorSummary();
      return () => { isMounted = false; };
    }, [selectedMarket]);

    useEffect(() => {
      // Load Market Overview (aggregates) for all categories once via a single API
      const fetchAggregates = async () => {
        // Fetch to guarantee order matching aggregateMarkets
        try {
          setAggLoading(true);
          const res = await apiClient.get('/summary/sector/aggregates', {
            params: { categories: aggregateMarkets.join(',') },
          });
          // Normalize market labels and exclude FOREX and ETF from the overview table
          const rows = Array.isArray(res?.data)
            ? res.data
                .map((d) => ({ ...d, market: (d.market || d.category || '').toString().toUpperCase() }))
                .filter((r) => r.market !== 'FOREX' && r.market !== 'ETF')
            : [];
            
          // Sort rows by the explicit order defined in aggregateMarkets
          rows.sort((a, b) => {
            const indexA = aggregateMarkets.indexOf(a.market);
            const indexB = aggregateMarkets.indexOf(b.market);
            // If not found in the array (e.g. unexpected), put them at the end
            const weightA = indexA !== -1 ? indexA : 999;
            const weightB = indexB !== -1 ? indexB : 999;
            return weightA - weightB;
          });

          setMarketAggregates(rows);
        } catch (e) {
          console.error('Failed to load market aggregates:', e);
          setMarketAggregates([]);
        } finally {
          setAggLoading(false);
        }
      };
      fetchAggregates();
      /* eslint-disable-next-line */
    }, []);

    const sortedSectorSummary = React.useMemo(() => {
      const rows = [...sectorSummary];
      const field = sectorSortField;
      rows.sort((a, b) => {
        let av, bv;
        switch (field) {
          case 'sector': av = a.sector || ''; bv = b.sector || ''; break;
          case 'outlook':
            av = (a.outlook ?? '').toString();
            bv = (b.outlook ?? '').toString();
            break;
          case 'total_symbols': av = Number(a.total_symbols) || 0; bv = Number(b.total_symbols) || 0; break;
          case 'avg_bullish_score': av = Number(a.avg_bullish_score) || 0; bv = Number(b.avg_bullish_score) || 0; break;
          case 'avg_bearish_score': av = Number(a.avg_bearish_score) || 0; bv = Number(b.avg_bearish_score) || 0; break;
          case 'created_at': av = new Date(a.created_at || 0).getTime(); bv = new Date(b.created_at || 0).getTime(); break;
          default: av = 0; bv = 0;
        }
        if (typeof av === 'string' && typeof bv === 'string') {
          return sectorSortDirection === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        }
        return sectorSortDirection === 'asc' ? (av - bv) : (bv - av);
      });
      return rows;
    }, [sectorSummary, sectorSortField, sectorSortDirection]);

    const sortedAggregates = React.useMemo(() => {
      const rows = [...marketAggregates];
      const field = aggSortField;
      if (!field) return rows;
      
      rows.sort((a, b) => {
        let av, bv;
        switch (field) {
          case 'market': av = a.market || ''; bv = b.market || ''; break;
          case 'outlook':
            av = (a.outlook ?? '').toString();
            bv = (b.outlook ?? '').toString();
            break;
          case 'total_symbols': av = Number(a.total_symbols) || 0; bv = Number(b.total_symbols) || 0; break;
          case 'avg_bullish_score': av = Number(a.avg_bullish_score) || 0; bv = Number(b.avg_bullish_score) || 0; break;
          case 'avg_bearish_score': av = Number(a.avg_bearish_score) || 0; bv = Number(b.avg_bearish_score) || 0; break;
          case 'created_at': av = new Date(a.created_at || 0).getTime(); bv = new Date(b.created_at || 0).getTime(); break;
          default: av = 0; bv = 0;
        }
        if (typeof av === 'string' && typeof bv === 'string') {
          return aggSortDirection === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        }
        return aggSortDirection === 'asc' ? (av - bv) : (bv - av);
      });
      return rows;
    }, [marketAggregates, aggSortField, aggSortDirection]);

    // Render sections as their data arrives; no global blocking spinner for Overview

    const predictionUI = (prediction) => {
      const base = 'inline-flex items-center gap-1 font-medium';
      if (prediction === 'UPWARD') {
        return (
          <span className={`${base} text-green-600 dark:text-green-400`}>
            <ArrowUpIcon className="h-4 w-4" /> Upward
          </span>
        );
      }
      if (prediction === 'DOWNWARD') {
        return (
          <span className={`${base} text-red-600 dark:text-red-400`}>
            <ArrowDownIcon className="h-4 w-4" /> Downward
          </span>
        );
      }
      return (
        <span className={`${base} text-gray-600 dark:text-gray-400`}>
          <ArrowRightIcon className="h-4 w-4" /> Neutral
        </span>
      );
    };

    const tableCls = theme === 'dark' 
      ? 'min-w-full text-sm divide-y divide-gray-700 bg-gray-900'
      : 'min-w-full text-sm divide-y divide-gray-200 bg-white';
    const tbodyCls = theme === 'dark' ? 'bg-gray-900 divide-y divide-gray-800' : 'bg-white divide-y divide-gray-200';
    // Group divider (vertical separator) between Overview and AI Outlook tables
    const groupDividerCls = `${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'} border-l-2`;

    // Render sentiment with color coding
    const sentimentUI = (value) => {
      const s = String(value || '')
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      let label = value || '-';
      let cls = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';

      if (s.includes('strong') && s.includes('bearish')) {
        cls = theme === 'dark' ? 'text-red-400' : 'text-red-500';
        label = 'Strong Bearish';
      } else if (s.includes('mild') && s.includes('bearish')) {
        cls = theme === 'dark' ? 'text-red-500' : 'text-red-800';
        label = 'Mild Bearish';
      } else if (s.includes('bearish')) {
        cls = theme === 'dark' ? 'text-red-300' : 'text-red-700';
        label = 'Bearish';
      } else if (s.includes('strong') && s.includes('bullish')) {
        cls = theme === 'dark' ? 'text-green-400' : 'text-green-600';
        label = 'Strong Bullish';
      } else if (s.includes('mild') && s.includes('bullish')) {
        cls = theme === 'dark' ? 'text-green-500' : 'text-green-800';
        label = 'Mild Bullish';
      } else if (s.includes('bullish')) {
        cls = theme === 'dark' ? 'text-green-300' : 'text-green-700';
        label = 'Bullish';
      } else if (s.includes('neutral')) {
        cls = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
        label = 'Neutral';
      }
      return <span className={`font-medium text-xs ${cls}`}>{label}</span>;
    };

    // displayNameFor and formatCET helpers are defined at module scope

    const sectorHandleSort = (field) => {
      if (sectorSortField === field) {
        setSectorSortDirection(sectorSortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSectorSortField(field);
        setSectorSortDirection('asc');
      }
    };

    const sectorGetSortIcon = (field) => {
      const isActive = sectorSortField === field;
      const cls = isActive ? 'text-blue-600' : 'text-gray-400';
      return (
        <svg className={`h-3 w-3 ${cls}`} viewBox="0 0 20 20" fill="currentColor">
          {sectorSortDirection === 'asc' ? (
            <path fillRule="evenodd" d="M5.293 11.293a1 1 0 011.414 0L10 14.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          ) : (
            <path fillRule="evenodd" d="M14.707 8.707a1 1 0 01-1.414 0L10 5.414 7.707 7.707A1 1 0 116.293 6.293l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
          )}
        </svg>
      );
    };

    const aggHandleSort = (field) => {
      if (aggSortField === field) {
        setAggSortDirection(aggSortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setAggSortField(field);
        setAggSortDirection('asc');
      }
    };

    const aggGetSortIcon = (field) => {
      const isActive = aggSortField === field;
      const cls = isActive ? 'text-blue-600' : 'text-gray-400';
      return (
        <svg className={`h-3 w-3 ${cls}`} viewBox="0 0 20 20" fill="currentColor">
          {aggSortDirection === 'asc' ? (
            <path fillRule="evenodd" d="M5.293 11.293a1 1 0 011.414 0L10 14.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          ) : (
            <path fillRule="evenodd" d="M14.707 8.707a1 1 0 01-1.414 0L10 5.414 7.707 7.707A1 1 0 116.293 6.293l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
          )}
        </svg>
      );
    };

    return (
      <div className="space-y-6">

        <SignInRequiredOverlay
          open={showSignInOverlay && !dismissedSignInOverlay}
          attemptedPath={signInFrom ? `${signInFrom.pathname || ''}${signInFrom.search || ''}` : ''}
          onDismiss={() => setDismissedSignInOverlay(true)}
          onSignIn={() => navigate('/signin', { state: { from: signInFrom } })}
          onSignUp={() => navigate('/signup', { state: { from: signInFrom } })}
        />

        {(aggLoading || marketAggregates.length >= 0) && (
          <div>
            <h2 className="text-xl font-semibold mb-3">Daily Market Outlook</h2>
            <div className={`overflow-x-auto rounded-lg border ${theme === 'dark' ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
              <table className={tableCls}>
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr>
                    <th colSpan={2} className="px-3 py-2 text-center text-[85%] font-bold">Market Overview</th>
                    <th colSpan={7} className={`px-3 py-2 text-center text-[85%] font-bold ${groupDividerCls}`}>AI Predictions</th>
                  </tr>
                  <tr>
                    <th className="px-3 py-2 text-center cursor-pointer text-[75%]" onClick={() => aggHandleSort('market')}>
                      <div className="flex items-center justify-center gap-1">Market {aggGetSortIcon('market')}</div>
                    </th>
                    <th className="px-3 py-2 text-center cursor-pointer text-[75%]" onClick={() => aggHandleSort('total_symbols')}>
                      <div className="flex items-center justify-center gap-1">Assets Tracked {aggGetSortIcon('total_symbols')}</div>
                    </th>
                    <th className={`px-3 py-2 text-center text-[75%] ${groupDividerCls}`}>Signals (model1)</th>
                    <th className="px-3 py-2 text-center text-[75%]">Signals (model2)</th>
                    <th className="px-3 py-2 text-center text-[75%]">Signals (model_ensemble)</th>
                    <th className="px-3 py-2 text-center cursor-pointer text-[75%]" onClick={() => aggHandleSort('avg_bullish_score')}>
                      <div className="flex items-center justify-center gap-1">Avg Bullish Score {aggGetSortIcon('avg_bullish_score')}</div>
                    </th>
                    <th className="px-3 py-2 text-center cursor-pointer text-[75%]" onClick={() => aggHandleSort('avg_bearish_score')}>
                      <div className="flex items-center justify-center gap-1">Avg Bearish Score {aggGetSortIcon('avg_bearish_score')}</div>
                    </th>
                    <th className="px-3 py-2 text-center cursor-pointer text-[75%]" onClick={() => aggHandleSort('outlook')}>
                      <div className="flex items-center justify-center gap-1">Outlook {aggGetSortIcon('outlook')}</div>
                    </th>
                    <th className="px-3 py-2 text-center cursor-pointer text-[75%]" onClick={() => aggHandleSort('created_at')}>
                      <div className="flex items-center justify-center gap-1">Updated (CET) {aggGetSortIcon('created_at')}</div>
                    </th>
                  </tr>
                </thead>
                <tbody className={tbodyCls}>
                  {aggLoading && (
                    <tr>
                      <td colSpan={9} className="px-4 py-6 text-center">
                        <div className="flex items-center justify-center">
                          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme === 'dark' ? 'border-blue-400' : 'border-blue-600'}`}></div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!aggLoading && sortedAggregates.map((r, idx) => (
                    <tr key={`${r.market}-${idx}`} className={theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}>
                      <td
                        className={`px-4 py-3 text-xs whitespace-nowrap cursor-pointer text-blue-600 dark:text-blue-400 hover:underline`}
                        onClick={() => {
                          // Navigate to Dashboard and pre-select market(s).
                          try { navigate(`/dashboard?market=${encodeURIComponent((r.market || r.category || '').toString().toUpperCase())}`); } catch {}
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          const url = `/dashboard?market=${encodeURIComponent((r.market || r.category || '').toString().toUpperCase())}`;
                          setTableCtxMenu({ open: true, xAbs: e.clientX, yAbs: e.clientY, url });
                        }}
                      >
                        {displayNameFor(r.market)}
                      </td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">{r.total_symbols}</td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">
                        {r.model_counts?.model1 ? (
                          <div className="flex items-center gap-3 text-xs">
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                              <ArrowUpIcon className="h-4 w-4" /> {r.model_counts.model1.upward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                              <ArrowDownIcon className="h-4 w-4" /> {r.model_counts.model1.downward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400">
                              <ArrowRightIcon className="h-4 w-4" /> {r.model_counts.model1.sideways}
                            </span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">
                        {r.model_counts?.model2 ? (
                          <div className="flex items-center gap-3 text-xs">
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                              <ArrowUpIcon className="h-4 w-4" /> {r.model_counts.model2.upward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                              <ArrowDownIcon className="h-4 w-4" /> {r.model_counts.model2.downward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400">
                              <ArrowRightIcon className="h-4 w-4" /> {r.model_counts.model2.sideways}
                            </span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">
                        {r.model_counts?.model_ensemble ? (
                          <div className="flex items-center gap-3 text-xs">
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                              <ArrowUpIcon className="h-4 w-4" /> {r.model_counts.model_ensemble.upward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                              <ArrowDownIcon className="h-4 w-4" /> {r.model_counts.model_ensemble.downward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400">
                              <ArrowRightIcon className="h-4 w-4" /> {r.model_counts.model_ensemble.sideways}
                            </span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">{r.avg_bullish_score != null ? Number(r.avg_bullish_score).toFixed(3) : '-'}</td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">{r.avg_bearish_score != null ? Number(r.avg_bearish_score).toFixed(3) : '-'}</td>
                      <td className="px-2 md:px-4 py-3 text-xs whitespace-nowrap">{sentimentUI(r.outlook)}</td>
                      <td className="px-2 md:px-4 py-3 text-xs whitespace-nowrap">{formatCET(r.created_at)}</td>
                    </tr>
                  ))}
                  {!aggLoading && sortedAggregates.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <h2 className="text-xl font-semibold mb-0">Daily Sector Outlook</h2>
        <div className="flex flex-wrap gap-2 mt-0 mb-4">
          {markets.map((m) => (
            <button
              key={m}
              onClick={() => setSelectedMarket(m)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                selectedMarket === m
                  ? 'bg-blue-600 text-white'
                  : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
              }`}
            >
              {displayNameFor(m)}
            </button>
          ))}
        </div>
        <div>
            <div className={`overflow-x-auto rounded-lg border ${theme === 'dark' ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
              <table className={tableCls}>
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr>
                    <th colSpan={2} className="px-3 py-2 text-center text-[85%] font-bold">Sector Overview</th>
                    <th colSpan={7} className={`px-3 py-2 text-center text-[85%] font-bold ${groupDividerCls}`}>AI Predictions</th>
                  </tr>
                  <tr>
                    <th className="px-3 py-2 text-center cursor-pointer  text-[75%]" onClick={() => sectorHandleSort('sector')}>
                      <div className="flex items-center justify-center gap-1">Sector {sectorGetSortIcon('sector')}</div>
                    </th>
                    <th className="px-3 py-2 text-center cursor-pointer  text-[75%]" onClick={() => sectorHandleSort('total_symbols')}>
                      <div className="flex items-center justify-center gap-1">Assets Tracked {sectorGetSortIcon('total_symbols')}</div>
                    </th>
                    <th className={`px-3 py-2 text-center text-[75%] ${groupDividerCls}`}>Signals (model1)</th>
                    <th className="px-3 py-2 text-center text-[75%]">Signals (model2)</th>
                    <th className="px-3 py-2 text-center text-[75%]">Signals (model_ensemble)</th>
                    <th className="px-3 py-2 text-center cursor-pointer text-[75%]" onClick={() => sectorHandleSort('avg_bullish_score')}>
                      <div className="flex items-center justify-center gap-1">Avg Bullish Score {sectorGetSortIcon('avg_bullish_score')}</div>
                    </th>
                    <th className="px-3 py-2 text-center cursor-pointer text-[75%]" onClick={() => sectorHandleSort('avg_bearish_score')}>
                      <div className="flex items-center justify-center gap-1">Avg Bearish Score {sectorGetSortIcon('avg_bearish_score')}</div>
                    </th>
                    <th className="px-3 py-2 text-center cursor-pointer text-[75%]" onClick={() => sectorHandleSort('outlook')}>
                      <div className="flex items-center justify-center gap-1">Outlook {sectorGetSortIcon('outlook')}</div>
                    </th>
                    <th className="px-3 py-2 text-center cursor-pointer  text-[75%]" onClick={() => sectorHandleSort('created_at')}>
                      <div className="flex items-center justify-center gap-1">Updated (CET) {sectorGetSortIcon('created_at')}</div>
                    </th>
                  </tr>
                </thead>
                <tbody className={tbodyCls}>
                  {sectorLoading && (
                    <tr>
                      <td colSpan={9} className="px-4 py-6 text-center">
                        <div className="flex items-center justify-center">
                          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme === 'dark' ? 'border-blue-400' : 'border-blue-600'}`}></div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!sectorLoading && sortedSectorSummary.map((r, idx) => (
                    <tr key={`${r.sector}-${idx}`} className={theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}>
                      <td
                        className={"px-4 py-3 text-xs whitespace-nowrap cursor-pointer text-blue-600 dark:text-blue-400 hover:underline"}
                        onClick={() => {
                          if (selectedMarket) {
                            const sector = r.sector || '';
                            navigate(`/dashboard?sector=${encodeURIComponent(sector)}&market=${encodeURIComponent(selectedMarket)}`);
                          }
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          if (selectedMarket) {
                            const sector = r.sector || '';
                            const url = `/dashboard?sector=${encodeURIComponent(sector)}&market=${encodeURIComponent(selectedMarket)}`;
                            setTableCtxMenu({ open: true, xAbs: e.clientX, yAbs: e.clientY, url });
                          }
                        }}
                      >
                        {r.sector}
                      </td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">{r.total_symbols}</td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">
                        {r.model_counts?.model1 ? (
                          <div className="flex items-center gap-3 text-xs">
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                              <ArrowUpIcon className="h-4 w-4" /> {r.model_counts.model1.upward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                              <ArrowDownIcon className="h-4 w-4" /> {r.model_counts.model1.downward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400">
                              <ArrowRightIcon className="h-4 w-4" /> {r.model_counts.model1.sideways}
                            </span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">
                        {r.model_counts?.model2 ? (
                          <div className="flex items-center gap-3 text-xs">
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                              <ArrowUpIcon className="h-4 w-4" /> {r.model_counts.model2.upward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                              <ArrowDownIcon className="h-4 w-4" /> {r.model_counts.model2.downward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400">
                              <ArrowRightIcon className="h-4 w-4" /> {r.model_counts.model2.sideways}
                            </span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">
                        {r.model_counts?.model_ensemble ? (
                          <div className="flex items-center gap-3 text-xs">
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                              <ArrowUpIcon className="h-4 w-4" /> {r.model_counts.model_ensemble.upward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                              <ArrowDownIcon className="h-4 w-4" /> {r.model_counts.model_ensemble.downward}
                            </span>
                            <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400">
                              <ArrowRightIcon className="h-4 w-4" /> {r.model_counts.model_ensemble.sideways}
                            </span>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">{r.avg_bullish_score != null ? r.avg_bullish_score.toFixed(3) : '-'}</td>
                      <td className="px-2 md:px-4 py-3 text-sm whitespace-nowrap">{r.avg_bearish_score != null ? r.avg_bearish_score.toFixed(3) : '-'}</td>
                      <td className="px-2 md:px-4 py-3 text-xs whitespace-nowrap">{sentimentUI(r.outlook)}</td>
                      <td className="px-2 md:px-4 py-3 text-xs whitespace-nowrap">{formatCET(r.created_at)}</td>
                    </tr>
                  ))}
                  {!sectorLoading && sortedSectorSummary.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {tableCtxMenu.open && (
              <div
                ref={tableMenuRef}
                className="z-50 min-w-[180px] rounded-md border shadow-lg text-sm bg-white dark:bg-gray-800 dark:border-gray-700"
                style={{
                  position: 'fixed',
                  left: (() => {
                    try {
                      const margin = 8;
                      const menuW = 220;
                      const desired = tableCtxMenu.xAbs || 0;
                      const maxLeft = Math.max(margin, window.innerWidth - menuW - margin);
                      return Math.min(Math.max(margin, desired), maxLeft);
                    } catch { return tableCtxMenu.xAbs || 0; }
                  })(),
                  top: (() => {
                    try {
                      const offset = 8;
                      const estimatedMenuH = 120;
                      const desired = (tableCtxMenu.yAbs || 0) + offset;
                      const maxTop = Math.max(8, window.innerHeight - estimatedMenuH - 8);
                      return Math.min(Math.max(8, desired), maxTop);
                    } catch { return tableCtxMenu.yAbs || 0; }
                  })(),
                }}
                role="menu"
              >
                <button
                  type="button"
                  className="block w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    try { window.open(tableCtxMenu.url, '_blank', 'noopener'); } catch {}
                    setTableCtxMenu({ open: false, xAbs: 0, yAbs: 0, url: '' });
                  }}
                  role="menuitem"
                >
                  Open in New Tab
                </button>
                <button
                  type="button"
                  className="block w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    try { window.open(tableCtxMenu.url, '_blank', 'noopener,width=1200,height=800'); } catch {}
                    setTableCtxMenu({ open: false, xAbs: 0, yAbs: 0, url: '' });
                  }}
                  role="menuitem"
                >
                  Open in New Window
                </button>
              </div>
            )}
        </div>
      </div>
    );
  };

// Models Dashboard Page (Best models summaries)
const ModelsDashboard = () => {
  const { theme } = useTheme();

  return <Dashboard theme={theme} />;
};

// Stock detail view under /summary?stock=...&market=...
export const StockDashboardDetail = () => {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const symbol = searchParams.get('stock');
  const market = searchParams.get('market');
  const [historical, setHistorical] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch predictions with selected model
  const { data: predictions, isLoading: predictionsLoading } = usePredictions({
    symbol,
    market,
    model: selectedModel || undefined,
    limit: 200
  });

  // Fetch trade signals with optional selected model filter
  const { data: tradeSignals } = useTradeSignals({
    symbol,
    model: selectedModel || undefined,
  });

  // Fetch stock details (name, current price, etc.) from backend
  const { data: stockInfo } = useStockDetail({ symbol, market });
  const { data: roiData } = useROI({ symbol, market });

  // Compute last close and percent change from historical locally
  let headerName = symbol || '';
  if (stockInfo?.name || stockInfo?.company_name) {
    headerName = stockInfo.name || stockInfo.company_name;
  }
  // Header close/percent: prefer today's close (live if market open),
  // otherwise fall back to the most recent historical close
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);


  // Fetch latest normalized day OHLC (with isMarketOpen) to drive header
  // Visibility-aware: pause polling when tab is inactive to preserve quotas
  const isAppVisible = useAppVisibility();

  useEffect(() => {
    let timer;
    const fetchQuote = async () => {
      if (!symbol) return;
      try {
        setQuoteError(null);
        const res = await apiClient.get('/quote', { params: { symbol, market } });
        setQuote(res.data || null);
      } catch (e) {
        setQuoteError(e);
      }
    };

    if (!symbol) return () => {};

    if (isAppVisible) {
      // Immediate refresh on becoming visible, then start interval
      fetchQuote();
      timer = setInterval(fetchQuote, 180000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [symbol, isAppVisible]);

  // Compute display close and percent vs previous close using the shared
  // pre-market/stale-quote helper (see utils/premarketQuote.js).  When the
  // provider's quote is stale (e.g. during pre-market it returns yesterday's
  // regular session), the latest DB candle wins, so the header keeps showing
  // the correct HLOC close from the database.
  const {
    displayClose: headerLastClose,
    pct: headerPct,
    badge: headerCloseBadge,
  } = React.useMemo(
    () => computeHeaderPrice(quote, Array.isArray(historical) ? historical : []),
    [historical, quote]
  );

  useEffect(() => {
    const load = async () => {
      if (!symbol) return;
      setLoading(true);
      setError('');
      try {
        // Default 3 months ~ 90 days
        const [hist] = await Promise.all([
          // Request all historical data (no range)
          apiClient.get(`/market/historical/${encodeURIComponent(symbol)}`, { params: { market } }),
        ]);
        setHistorical(hist?.data || []);
      } catch (e) {
        console.error('Failed to load historical:', e);
        setError('Failed to load historical data');
        setHistorical([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [symbol, market]);

  // Update the browser tab title to include stock symbol (and name if available)
  useEffect(() => {
    try {
      if (symbol) {
        const name = stockInfo?.name || stockInfo?.company_name || '';
        document.title = name ? `${name} (${symbol}) - BorsAI` : `${symbol} - BorsAI`;
      } else {
        document.title = 'BorsAI';
      }
    } catch {}
  }, [symbol, stockInfo?.name, stockInfo?.company_name]);

  // Update available models when predictions data changes
  // Populate available models and default selection from stockInfo.model_stats
  useEffect(() => {
    const ms = stockInfo?.model_stats;
    if (ms) {
      const models = Array.isArray(ms.available_models) ? ms.available_models : [];
      setAvailableModels(models);
      if (ms.best_model_12_months) {
        setSelectedModel(ms.best_model_12_months);
      } else if (models.length) {
        setSelectedModel(models[0]);
      }
    } else {
      setAvailableModels([]);
      setSelectedModel('');
    }
  }, [stockInfo?.model_stats]);

  if (!symbol) return <div className="text-sm text-gray-500">No symbol selected.</div>;
  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-sm text-red-500">{error}</div>;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h2 className="text-lg font-semibold leading-tight">
            {headerName} {symbol ? `(${symbol})` : ''} {' '}
            {headerCloseBadge && (
              <span
                className={
                  headerCloseBadge === 'LIVE'
                    ? 'ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                    : 'ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                }
                title={headerCloseBadge === 'LIVE' ? 'Live intraday close (updates periodically)' : headerCloseBadge === 'TODAY' ? 'Today’s last known close' : 'Previous trading day close'}
              >
                {headerCloseBadge === 'PREV' ? 'PREV CLOSE' : headerCloseBadge}
              </span>
            )}
            {' '}
            <span className="text-base font-medium">{headerLastClose != null ? `$${headerLastClose.toFixed(2)}` : '-'}</span>{' '}
            {headerPct != null && (
              <span className={`text-sm font-medium ${headerPct > 0 ? 'text-green-600 dark:text-green-400' : headerPct < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                ({headerPct.toFixed(2)}%)
              </span>
            )}
          </h2>
          {/* Extended session info for US equities */}
          {(() => {
            try {
              const sess = quote?.session;
              const extP = typeof quote?.extendedPrice === 'number' ? quote.extendedPrice : null;
              const extPct = typeof quote?.extendedChangePercent === 'number' ? quote.extendedChangePercent : null;
              if (!sess) return null;
              const lineCls = 'mt-1 text-xs';
              if (sess === 'PRE' && extP != null) {
                return (
                  <div className={lineCls}>
                    Pre Market {quote?.extendedIsLive ? '(live)' : ''} ${extP.toFixed(2)} {extPct != null ? (<span className={extPct>0?'text-green-600 dark:text-green-400':extPct<0?'text-red-600 dark:text-red-400':'text-gray-500 dark:text-gray-400'}>({extPct.toFixed(2)}%)</span>) : null}
                  </div>
                );
              }
              if (sess === 'POST' && extP != null) {
                return (
                  <div className={lineCls}>
                    Post Market {quote?.extendedIsLive ? '(live)' : ''}: ${extP.toFixed(2)} {extPct != null ? (<span className={extPct>0?'text-green-600 dark:text-green-400':extPct<0?'text-red-600 dark:text-red-400':'text-gray-500 dark:text-gray-400'}>({extPct.toFixed(2)}%)</span>) : null}
                  </div>
                );
              }
              if (sess === 'CLOSED' && extP != null) {
                // Show after-hours snapshot in late evening (server computes availability); not shown overnight before pre-market
                return (
                  <div className={lineCls}>
                    Post Market: ${extP.toFixed(2)} {extPct != null ? (<span className={extPct>0?'text-green-600 dark:text-green-400':extPct<0?'text-red-600 dark:text-red-400':'text-gray-500 dark:text-gray-400'}>({extPct.toFixed(2)}%)</span>) : null}
                  </div>
                );
              }
            } catch {}
            return null;
          })()}
          {(stockInfo?.gics_sector || stockInfo?.gics_industry_group) && (
            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              {stockInfo?.gics_sector || ''}
              {stockInfo?.gics_industry_group ? `${stockInfo?.gics_sector ? ' / ' : ''}${stockInfo.gics_industry_group}` : ''}
            </div>
          )}
        </div>

        {/* Model Selector */}
        {/* Model selection moved into chart toolbar next to Buy/Sell */}
      </div>

      <div className={`p-3 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <CandlestickChart
          data={historical}
          indicators={{}}
          predictions={predictions}
          tradeSignals={tradeSignals}
          theme={theme}
          symbol={symbol}
          market={market}
          height={480}
          availableModels={availableModels}
          selectedModel={selectedModel}
          onChangeModel={(m) => setSelectedModel(m)}
          stockInfo={stockInfo}
        />
      </div>

      {/* Under-chart explanatory row */}
      <div className="mt-2 flex flex-col sm:flex-row sm:items-start sm:justify-between text-xs">
        {/* Left: description and tip */}
        <div className="text-left text-gray-600 dark:text-gray-400 sm:pr-4">
          {(() => {
            const modelLabel = selectedModel || predictions?.model || 'Model';
            const latestDoc = (Array.isArray(predictions?.docs) && predictions.docs.length)
              ? predictions.docs[predictions.docs.length - 1]
              : null;
            const days = (latestDoc?.preds?.length) || (Array.isArray(predictions?.preds) ? predictions.preds.length : 0) || 0;
            return (
              <>
                <div>
                  {modelLabel} price prediction for the next {days} {days === 1 ? 'day' : 'days'} for {symbol}. Prices include extended session data where available.
                </div>
                <div className="mt-1">💡 Tip: Hover on historical data to see previous predictions for comparison.</div>
              </>
            );
          })()}
        </div>
        {/* Right: last updated */}
        <div className="sm:mt-0 mt-2 sm:text-right shrink-0 whitespace-nowrap text-gray-600 dark:text-gray-400">
          {(() => {
            const latestCreated = predictions?.created_at || (Array.isArray(predictions?.docs) && predictions.docs.length ? predictions.docs[predictions.docs.length - 1]?.created_at : null);
            const formatted = formatCET(latestCreated);
            return <span>AI signals and Technical Indicators Updated: {formatted} CET</span>;
          })()}
        </div>
      </div>

      {/* Daily Comment from LLM Technical Analysis */}
      <DailyComment data={stockInfo?.llm_tech_interpret} createdAt={stockInfo?.created_at || stockInfo?.last_updated} theme={theme} />

      {/* ROI Performance Table */}
      <RoiPerformanceTable data={roiData || []} model={selectedModel || predictions?.model} theme={theme} stockName={headerName} />

      {/* Statistics from syminfodb-insight */}
      <div className={`rounded-lg border mt-4 p-4 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className="text-lg font-semibold mb-3">Statistics</h3>
        <StockStatisticsAccordion stock={stockInfo} />
      </div>

      {/* Model Metrics */}
      <div className={`rounded-lg border mt-4 p-4 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className="text-base font-semibold mb-2">Model Metrics</h4>
        <ModelMetricsTable metrics={stockInfo?.model_metrics || {}} theme={theme} />
      </div>

      {/* Model Methodology Section */}
      <div className={`rounded-lg border mt-4 p-4 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h4 className="text-base font-semibold mb-2">Model Methodology</h4>
        <p className="text-gray-600 dark:text-gray-400 text-left">
          {stockInfo?.ai_comment || "No Model Methodology is available."}
        </p>
      </div>
    </div>
  );
};


// Settings Component
import IndicatorSettings from './components/IndicatorSettings';
const Settings = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    theme_preference: 'dark',
    email_notifications: true,
    market_alerts: true,
    preferred_markets: ['NASDAQ', 'NYSE']
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      // Use shared axios client with cookie-based auth interceptor
      const { default: client } = await import('./api/client');
      const response = await client.get('/user/settings');
      setSettings((prev) => ({ ...prev, ...(response?.data || {}) }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    setSuccess(false);
    try {
      const { default: client } = await import('./api/client');
      await client.put('/user/settings', settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const markets = ['SP500', 'DJI', 'NASDAQ', 'NYSE', 'CRYPTO', 'ETF', 'FOREX', 'COMMODITIES', 'BIST', 'USALL'];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Settings</h2>

      <div className={`p-6 rounded-lg border ${
        theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
      }`}>
        <h3 className="text-lg font-semibold mb-4">Preferences</h3>

        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            Settings saved successfully!
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.email_notifications}
                onChange={(e) => setSettings({
                  ...settings,
                  email_notifications: e.target.checked
                })}
                className="mr-2"
              />
              Email Notifications
            </label>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.market_alerts}
                onChange={(e) => setSettings({
                  ...settings,
                  market_alerts: e.target.checked
                })}
                className="mr-2"
              />
              Market Alerts
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Preferred Markets
            </label>
            <div className="space-y-2">
              {markets.map((market) => (
                <label key={market} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.preferred_markets.includes(market)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSettings({
                          ...settings,
                          preferred_markets: [...settings.preferred_markets, market]
                        });
                      } else {
                        setSettings({
                          ...settings,
                          preferred_markets: settings.preferred_markets.filter(m => m !== market)
                        });
                      }
                    }}
                    className="mr-2"
                  />
                  {displayNameFor(market)}
                </label>
              ))}
            </div>
          </div>

          <button
            onClick={saveSettings}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Indicator style settings */}
      <div className={`p-6 rounded-lg border ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <h3 className="text-lg font-semibold mb-4">Chart Indicator Styles</h3>
        <IndicatorSettings theme={theme} />
      </div>
    </div>
  );
};

// Subscription Component
const Subscription = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handlePayment = async () => {
    setLoading(true);
    try {
      const response = await apiClient.post('/payment/create-order', {
        amount: '29.99',
        currency: 'USD',
        description: 'BorsAI Premium Subscription'
      });

      // In a real app, redirect to PayPal
      alert(`Payment order created: ${response.data.order_id}\nThis is a demo - no actual payment will be processed.`);
    } catch (error) {
      console.error('Payment failed:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Subscription</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Free Plan */}
        <div className={`p-6 rounded-lg border ${
          user?.subscription_status === 'free'
            ? 'border-blue-500 ring-2 ring-blue-200'
            : theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
        }`}>
          <h3 className="text-xl font-semibold mb-2">Free Plan</h3>
          <div className="text-3xl font-bold mb-4">$0<span className="text-sm font-normal">/month</span></div>
          <ul className="space-y-2 mb-6">
            <li>✓ Basic market data</li>
            <li>✓ Limited predictions</li>
            <li>✓ 5 watchlist items</li>
          </ul>
          {user?.subscription_status === 'free' && (
            <div className="text-green-600 font-medium">Current Plan</div>
          )}
        </div>

        {/* Premium Plan */}
        <div className={`p-6 rounded-lg border ${
          user?.subscription_status === 'premium'
            ? 'border-blue-500 ring-2 ring-blue-200'
            : theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
        }`}>
          <h3 className="text-xl font-semibold mb-2">Premium Plan</h3>
          <div className="text-3xl font-bold mb-4">$29.99<span className="text-sm font-normal">/month</span></div>
          <ul className="space-y-2 mb-6">
            <li>✓ Real-time market data</li>
            <li>✓ Advanced AI predictions</li>
            <li>✓ Unlimited watchlist</li>
            <li>✓ Priority support</li>
            <li>✓ Mobile app access</li>
          </ul>
          {user?.subscription_status === 'premium' ? (
            <div className="text-green-600 font-medium">Current Plan</div>
          ) : (
            <button
              onClick={handlePayment}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Upgrade to Premium'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// Main App Component
const AppContent = () => {
  const { theme } = useTheme();
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const location = useLocation();

  // Update activeTab based on current route
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      setActiveTab('dashboard');
    } else if (location.pathname === '/') {
      setActiveTab('overview');
    } else if (location.pathname === '/statistics') {
      setActiveTab('statistics');
    } else if (location.pathname === '/settings') {
      setActiveTab('settings');
    } else if (location.pathname === '/portfolio') {
      setActiveTab('portfolio');
    } else {
      // For routes like /summary, don't highlight a primary tab
      setActiveTab('');
    }
  }, [location.pathname]);

  // Ensure page title resets on non-stock pages (override any stale stock title)
  useEffect(() => {
    try {
      const isDashboard = location.pathname === '/dashboard';
      const params = new URLSearchParams(location.search); // Get URL parameters
      const isSummaryStock = location.pathname === '/summary' && params.get('stock') && params.get('market');
      if (isSummaryStock) return; // Stock page manages its own title

      let title = 'BorsAI';
      if (location.pathname === '/about') title = 'About - BorsAI';
      else if (location.pathname === '/contact') title = 'Contact - BorsAI';
      else if (location.pathname === '/privacy-policy') title = 'Privacy Policy - BorsAI';
      else if (location.pathname === '/cookie-policy') title = 'Cookie Policy - BorsAI';
      else if (location.pathname === '/terms-of-service') title = 'Terms of Service - BorsAI';
  else if (isDashboard) title = 'Dashboard - BorsAI';
      else if (location.pathname === '/') title = 'BorsAI';
      document.title = title;
    } catch {}
  }, [location.pathname, location.search]);

  // Keep header/footer visible even while auth loads; show spinner only in main area

  // if (!user) {
  //   return <LoginForm />;
  // }

  const renderContent = () => {
    // Route-aware rendering
    if (location.pathname === '/dashboard') {
      return <ModelsDashboard />;
    }
    if (location.pathname === '/summary') {
      return <StockDashboardDetail />;
    }
    if (location.pathname === '/settings') {
      return <Settings />;
    }
    if (location.pathname === '/portfolio') {
      return <Portfolio />;
    }
    // Home page ("/") shows Overview
    if (location.pathname === '/') {
      return <Overview />;
    }
    if (location.pathname === '/statistics') {
      return <Statistics theme={theme} />;
    }
    if (location.pathname === '/about') {
      return <About />;
    }
    if (location.pathname === '/contact') {
      return <Contact />;
    }
    if (location.pathname === '/privacy-policy') {
      return <PrivacyPolicy />;
    }
    if (location.pathname === '/cookie-policy') {
      return <CookiePolicy />;
    }
    if (location.pathname === '/terms-of-service') {
      return <TermsOfService />;
    }
    switch (activeTab) {
      case 'dashboard':
        return <ModelsDashboard />;
      case 'about':
        return <About />;
      case 'contact':
        return <Contact />;
      case 'settings':
        return <Settings />;
      case 'subscription':
        return <Subscription />;
      case 'portfolio':
        return <Portfolio />;
      default:
        return <Overview />;
    }
  };

  return (
    <div className={`min-h-screen ${
      theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      <Header onMenuToggle={() => setSidebarOpen(true)} setActiveTab={setActiveTab} />

      <div className="flex">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

  <main className="flex-1 p-2 lg:ml-[13.5rem]">
          {loading ? <LoadingSpinner /> : renderContent()}
        </main>
      </div>

      {/* Global footer disclaimer under all pages */}
      <FooterDisclaimer />
    </div>
  );
};

// Main App
function App() {
  const queryClient = new QueryClient();
  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <ScrollToTop />
            <div className="App">
              <Routes>
                {/*
                  Legal pages are always reachable.
                  - If consent exists, render with full app chrome via AppContent (so user can navigate back).
                  - If no consent, render standalone legal pages (no access to app).
                */}
                <Route
                  path="/privacy-policy"
                  element={hasEssentialConsent() ? <AppContent /> : <PrivacyPolicy />}
                />
                <Route
                  path="/cookie-policy"
                  element={hasEssentialConsent() ? <AppContent /> : <CookiePolicy />}
                />
                <Route
                  path="/terms-of-service"
                  element={hasEssentialConsent() ? <AppContent /> : <TermsOfService />}
                />

                {/* Consent gate applies to auth + app */}
                <Route
                  path="/signin"
                  element={
                    <CookieConsentGate>
                      <SignIn />
                    </CookieConsentGate>
                  }
                />
                <Route
                  path="/signup"
                  element={
                    <CookieConsentGate>
                      <SignUp />
                    </CookieConsentGate>
                  }
                />
                <Route
                  path="/confirm-account"
                  element={
                    <CookieConsentGate>
                      <ConfirmAccount />
                    </CookieConsentGate>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <CookieConsentGate>
                      <ForgotPassword />
                    </CookieConsentGate>
                  }
                />

                {/* Public pages: allow browsing without authentication */}
                <Route
                  path="/"
                  element={
                    <CookieConsentGate>
                      <AppContent />
                    </CookieConsentGate>
                  }
                />
                <Route
                  path="/summary"
                  element={
                    <CookieConsentGate>
                      <AppContent />
                    </CookieConsentGate>
                  }
                />
                <Route
                  path="/about"
                  element={
                    <CookieConsentGate>
                      <AppContent />
                    </CookieConsentGate>
                  }
                />
                <Route
                  path="/contact"
                  element={
                    <CookieConsentGate>
                      <AppContent />
                    </CookieConsentGate>
                  }
                />

                {/* Public pages: allow browsing without authentication */}
                <Route
                  path="/dashboard"
                  element={
                    <CookieConsentGate>
                      <AppContent />
                    </CookieConsentGate>
                  }
                />
                <Route
                  path="/statistics"
                  element={
                    <CookieConsentGate>
                      <AppContent />
                    </CookieConsentGate>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <CookieConsentGate>
                      <AppContent />
                    </CookieConsentGate>
                  }
                />

                {/* Auth-required page: Portfolio */}
                <Route
                  path="/portfolio"
                  element={
                    <CookieConsentGate>
                      <PrivateRoute>
                        <AppContent />
                      </PrivateRoute>
                    </CookieConsentGate>
                  }
                />

                <Route
                  path="*"
                  element={<Navigate to="/" replace />}
                />
              </Routes>
            </div>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </Router>
  );
}

export default App;